import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import TutorWeightsDemo from './components/TutorWeightsDemo'
import './App.css'
import DevarshAgent from './components/DevarshAgent'
import ErrorBoundary from './components/ErrorBoundary'
import ScrollFX, { RotatingVerb, Manifesto, StackList, ProofFeed } from './components/ScrollFX'

const LOAD_STEPS = ['Discover', 'Prototype', 'Evaluate', 'Guardrail', 'Ship']

// Everything above the fold, fetched while the loader runs so the hero
// appears fully painted the instant the loader dissolves.
export const HERO_IMG = `/devarsh-cutout.webp?v=${__BUILD_ID__}`
const CRITICAL_ASSETS = [
  HERO_IMG,
  '/mindset-copilot-logo.png', '/presence-logo.png', '/umbc-logo.png',
  '/barclays-logo.png', '/byjus-logo.png', '/opportune-logo.png',
]
const preload = (src) => new Promise((resolve) => {
  const img = new Image()
  img.onload = img.onerror = () => resolve()
  img.src = src
})

// Boot sequence that mirrors how I work: discover, prototype, ship. The
// monogram morphs DW -> PM as the counter climbs, then the screen dissolves.
const LoadingScreen = ({ onComplete }) => {
  const [pct, setPct] = useState(0)
  const [exit, setExit] = useState(false)
  const doneRef = useRef(onComplete)
  doneRef.current = onComplete

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const DURATION = reduce ? 900 : 2700
    // Driven by a wall-clock interval, not requestAnimationFrame: browsers
    // pause rAF in background/hidden tabs, which left visitors stuck at 0%.
    const start = Date.now()
    let exitTimer = 0
    let finished = false
    let assetsReady = false
    let timeUp = false
    const finish = () => {
      if (finished) return
      finished = true
      clearInterval(iv)
      setPct(100)
      setExit(true)
      exitTimer = setTimeout(() => doneRef.current(), 650)
    }
    const tryFinish = () => { if (assetsReady && timeUp) finish() }
    // Fetch the hero and first-screen logos in parallel with the animation.
    Promise.all(CRITICAL_ASSETS.map(preload)).then(() => { assetsReady = true; tryFinish() })
    const iv = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / DURATION)
      // Hold at 96% if the images are still arriving on a slow connection.
      setPct(Math.min(assetsReady ? 100 : 96, Math.round((1 - Math.pow(1 - t, 2.4)) * 100)))
      if (t >= 1) { timeUp = true; tryFinish() }
    }, 40)
    // Hard stop so nothing can keep the site hidden behind the loader.
    const safety = setTimeout(() => { assetsReady = true; timeUp = true; finish() }, DURATION + 4000)
    return () => {
      clearInterval(iv)
      clearTimeout(safety)
      clearTimeout(exitTimer)
    }
  }, [])

  const first = pct >= 60 ? 'P' : 'D'
  const second = pct >= 30 ? 'M' : 'W'

  return (
    <div className={`ld ${exit ? 'is-exit' : ''}`}>
      <div className="ld-mesh" aria-hidden="true"><i></i><i></i><i></i></div>
      <div className="ld-center">
        <div className="ld-mark">
          <span className="ld-ring" aria-hidden="true"></span>
          <span className="ld-tile">
            <span className="ld-letter" key={first}>{first}</span>
            <span className="ld-letter" key={second + 'b'}>{second}</span>
          </span>
        </div>
        <div className="ld-count" aria-live="off"><b>{pct}</b><small>%</small></div>
        <div className="ld-bar"><i style={{ transform: `scaleX(${pct / 100})` }}></i></div>
        <ol className="ld-steps">
          {LOAD_STEPS.map((label, i) => {
            const n = LOAD_STEPS.length
            const at = Math.round((i / n) * 100)
            const end = Math.round(((i + 1) / n) * 100)
            const state = pct >= end ? 'done' : pct >= at ? 'active' : ''
            return (
              <li key={label} className={state}>
                <span className="ld-check" aria-hidden="true">{state === 'done' ? '✓' : ''}</span>
                {label}
              </li>
            )
          })}
        </ol>
      </div>
      <div className="ld-foot">Devarsh Wali <span>/</span> Product Manager</div>
    </div>
  )
}

const TypewriterText = ({ text, delay = 50, className = "" }) => {
  const [displayText, setDisplayText] = useState("")
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex])
        setCurrentIndex(prev => prev + 1)
      }, delay)
      return () => clearTimeout(timer)
    }
  }, [currentIndex, text, delay])

  return <span className={className}>{displayText}<span className="cursor">|</span></span>
}

const MagicalLogo = ({ children }) => {
  return (
    <div className="nav-logo-container">
      {children}
    </div>
  )
}

const experienceData = [
  {
    id: 1,
    company: "Mindset Copilot",
    role: "AI Product Manager (Contract)",
    period: "Mar 2026 - Aug 2026",
    icon: "/mindset-copilot-logo.png",
    isLogoIcon: true,
    link: "https://www.mindsetcopilot.com/",
    description: [
      "Shipped 0-to-1 AI-powered MVP in 3 weeks using Replit, taking the product from customer discovery to educator-facing workflows with prompt logic and AI-guided user journeys",
      "Designed human-in-the-loop agentic PM workflow using LangGraph, Granola transcripts, and Jira MCP to turn customer calls into prioritized Jira tasks, decision logs, and risk flags",
      "Defined AI eval criteria with human review loops tracking output accuracy, relevance, hallucination risk, and user friction to ensure safe, reliable educator workflows"
    ],
    achievements: ["0-to-1 AI Product", "MVP in 3 Weeks", "LangGraph Agentic Workflow", "AI Eval Criteria", "Human-in-the-Loop Design", "Replit Prototyping"],
    metrics: [
      { value: "<2%", label: "Hallucination Rate" },
      { value: "95%", label: "Product Adoption" },
      { value: "3 Schools", label: "Pilot MVP" }
    ]
  },
  {
    id: 2,
    company: "Presence",
    role: "MBA Fellow (Product & Strategy)",
    period: "Oct 2025 - Dec 2025",
    icon: "/presence-logo.png",
    isLogoIcon: true,
    link: "https://www.presence.com/",
    description: [
      "Automated weekly Leadership Team metrics reporting, saving 200+ hours/month through Excel VBA and Tableau dashboards",
      "Improved clinician onboarding completion from 54% to 66% through iterative workflow redesign and cross-functional experimentation",
      "Owned product discovery for ML-driven referral routing model and built SQL/Tableau dashboards tracking activation and adoption"
    ],
    achievements: ["ML-Driven Referral Routing", "Onboarding Optimization", "SQL & Tableau Dashboards", "Product Requirements", "Cross-functional Partnering"],
    metrics: [
      { value: "200+ hrs/mo", label: "Reporting Time Saved" },
      { value: "54% to 66%", label: "Onboarding Completion" },
      { value: "$100M+", label: "ARR SaaS Product" }
    ]
  },
  {
    id: 3,
    company: "UMBC",
    role: "Program Manager",
    period: "Jan 2025 - Dec 2025",
    icon: "/umbc-logo.png",
    isLogoIcon: true,
    link: "https://www.umbc.edu/",
    description: [
      "Launched a career support chatbot for 10K+ students, defining intents, routing, escalations and permissions across the Career Center",
      "Established an experimentation framework with 12+ A/B tests, KPIs and event tracking to spot drop-offs and improve placement outcomes",
      "Drove rollout through UAT, training and post-launch triage, achieving 92% NPS from students and advisors"
    ],
    achievements: ["Chatbot for 10k+ Students", "A/B Testing Framework", "UAT & Launch", "Product Discovery", "KPIs & Event Tracking"],
    metrics: [
      { value: "10K+", label: "Students Supported" },
      { value: "12+", label: "A/B Tests" },
      { value: "92%", label: "NPS Achieved" }
    ]
  },
  {
    id: 4,
    company: "Barclays",
    role: "Product Manager",
    period: "Mar 2023 - Jan 2024",
    icon: "/barclays-logo.png",
    isLogoIcon: true,
    link: "https://www.barclays.com/",
    description: [
      "Lifted NPS +7.6 points by redesigning compliance-aware IVR workflows with intent matching and skills-based routing, reducing wait times and wrong transfers",
      "Led agile ceremonies and sprint planning across 3 globally distributed scrum teams, owning the product roadmap and backlog prioritization",
      "Implemented standardized AI-powered monitoring and analytics across 35+ global business units, driving consistent performance tracking and unified reporting",
      "Owned product strategy for payment operations and disbursement workflows supporting $110M+ in payment volume across U.S. and U.K. markets"
    ],
    achievements: ["NPS +7.6 Lift", "IVR Redesign", "3 Scrum Teams Led", "AI-Powered Monitoring", "35+ Business Units", "$110M+ Payment Volume"],
    metrics: [
      { value: "+7.6", label: "NPS Lift" },
      { value: "3", label: "Scrum Teams Led" },
      { value: "35+", label: "Business Units" }
    ]
  },
  {
    id: 5,
    company: "BYJU'S",
    role: "Product Manager",
    period: "Aug 2021 - Mar 2023",
    icon: "/byjus-logo.png",
    isLogoIcon: true,
    link: "https://byjus.com/",
    description: [
      "Launched BYJU'S English 0 to 1, scaling it to $1.2M+ monthly revenue in 10 months on an app with 100M+ registered learners",
      "Managed a 14-person cross-functional squad across Design, Engineering, Data Science, Sales, Marketing and Content",
      "Owned Mixpanel analytics and built Tableau dashboards to monitor usage, retention, engagement and NPS trends across products"
    ],
    achievements: ["$1.2M+ MRR", "14-Person Squad", "Mixpanel Analytics", "Tableau Dashboards", "0-to-1 Launch", "GTM & Retention"],
    metrics: [
      { value: "$1.2M+", label: "Monthly Revenue" },
      { value: "14", label: "Person Squad" },
      { value: "100M+", label: "Registered Learners" }
    ]
  },
  {
    id: 6,
    company: "Opportune Hospitality Services",
    role: "Software Engineer",
    period: "Jan 2019 - May 2021",
    icon: "/opportune-logo.png",
    isLogoIcon: true,
    link: "",
    description: [
      "Led engineering and launch of 20+ core workflow features as tech lead, aligning stakeholders and delegating tasks across the team",
      "Transformed manual hotel operations into digital ticketing and automated assignment flows, improving service resolution and staff accountability",
      "Built PowerBI dashboards and event tracking to monitor adoption and churn, scaling the SaaS platform to $60K+ ARR with 94%+ app NPS"
    ],
    achievements: ["20+ Workflow Launches", "Tech Lead", "PM & Engineer Dual Role", "Digital Ticketing", "Workflow Mapping"],
    metrics: [
      { value: "20+", label: "Features Launched" },
      { value: "94%+", label: "App NPS" },
      { value: "$60K+", label: "ARR Scaled" }
    ]
  }
]

const educationData = [
  {
    id: 1,
    degree: "MS in Engineering Management",
    university: "University of Maryland, Baltimore County (UMBC)",
    link: "https://www.umbc.edu/",
    period: "Dec 2025",
    icon: "/umbc-logo.png",
    isLogoIcon: true,
    description: "Pursued masters studies in engineering management with focus on product leadership, strategic planning and organizational excellence. Earned a Project Management certification alongside the degree. Maintained exceptional academic performance while building practical skills in project management and business strategy.",
    metrics: [
      { value: "3.87", label: "GPA" },
      { value: "Dec 2025", label: "Graduation" },
      { value: "UMBC", label: "University" }
    ],
    achievements: ["Project Management", "Strategic Planning", "Product Leadership", "Business Strategy", "Graduate Assistant", "Engineering Management"]
  },
  {
    id: 2,
    degree: "BE in Electrical Engineering",
    university: "Pune University",
    link: "http://www.unipune.ac.in/",
    period: "May 2021",
    icon: "/pune-university-logo.png",
    isLogoIcon: true,
    description: "Completed undergraduate degree in Electrical Engineering with comprehensive coursework in power systems, control systems, and embedded systems. Developed strong foundations in circuit analysis, electromagnetics, and power electronics. Actively participated in technical projects and competitions.",
    metrics: [
      { value: "3.20", label: "CGPA" },
      { value: "May 2021", label: "Graduation" },
      { value: "Pune", label: "University" }
    ],
    achievements: ["Circuit Analysis", "Power Systems", "Control Systems", "Embedded Systems", "Power Electronics", "Technical Leadership"]
  }
]

const projectsData = [
  {
    id: 7,
    title: "TutorWeights - AI Tutor Eval",
    demo: true,
    description: (
      <ul className="enhanced-bullets">
        <li>Built an evaluation tool that tests whether an AI tutor actually teaches before it launches. It simulates multi-turn student conversations from personas, hidden misconceptions and topics, using synthetic scenarios only and no real student data.</li>
        <li>Scores every conversation on a seven-part pedagogy rubric: correctness, scaffolding, misconception handling, over-answer control, tone, age appropriateness and safety. Results roll up into a 0 to 100 launch readiness score with failure tags and prioritized fixes.</li>
        <li>Rerun after every prompt change to catch regressions. In the demo, a baseline tutor prompt that hands over the answer scores 47 and a prompt with pedagogy guardrails scores 95, and the failure tags show exactly why.</li>
      </ul>
    ),
    tech: ["Next.js", "TypeScript", "SQLite", "Rubric-based LLM Scoring", "Synthetic Scenario Engine", "Anthropic API", "OpenAI API"],
    tools: ["Claude", "Railway", "GitHub"],
    metrics: [
      { value: "0-100", label: "Launch Readiness Score" },
      { value: "7", label: "Rubric Dimensions" },
      { value: "47 to 95", label: "Demo Baseline vs Guarded" }
    ],
    link: "#"
  },
  {
    id: 1,
    title: "AppliFirst",
    description: (
      <ul className="enhanced-bullets">
        <li>Built a personal full-stack agentic SaaS product on Replit, shipping a real-time job board plus Kanban pipeline using React, TypeScript, Node/Express, PostgreSQL, and Drizzle ORM, moving from messy requirements to shippable workflows fast.</li>
        <li>Integrated OpenAI via REST APIs to turn unstructured inputs into structured outputs, including job summarization and resume tailoring/analyzer flows, with deterministic schemas, guardrails, and human review steps for accuracy.</li>
        <li>Delivered end-to-end integrations like a Chrome Extension (Manifest V3) for data capture, secure auth and ingestion endpoints, and event tracking to measure activation and drop-offs, then iterated weekly based on what the data said.</li>
      </ul>
    ),
    images: [
      { src: "/applifirst-1.png", alt: "AppliFirst Landing Page" },
      { src: "/applifirst-2.png", alt: "AppliFirst How It Works" },
      { src: "/applifirst-3.png", alt: "AppliFirst Founder Quote" },
      { src: "/applifirst-4.png", alt: "AppliFirst Job Board Dashboard" },
      { src: "/applifirst-5.png", alt: "AppliFirst Kanban Pipeline" },
      { src: "/applifirst-6.png", alt: "AppliFirst Chrome Extension" },
      { src: "/applifirst-7.png", alt: "AppliFirst Help Center" }
    ],
    tech: ["React", "TypeScript", "Node.js", "Express", "PostgreSQL", "Drizzle ORM", "OpenAI API", "Chrome Extension (Manifest V3)"],
    tools: ["Replit", "GitHub", "Postman", "OpenAI", "PostgreSQL", "Drizzle Studio"],
    metrics: [
      { value: "20+", label: "Features Shipped" },
      { value: "AI-Powered", label: "Resume Analyzer" },
      { value: "0 to 1", label: "Product Launch" }
    ],
    link: "https://applifirst-production.up.railway.app/",
    isLive: true
  },
  {
    id: 2,
    title: "ProdigyQuest",
    description: (
      <ul className="enhanced-bullets">
        <li>Ideated and launched a beta version of a gamified K–12 learning platform that makes studying addictive and effective through personalized learning paths, progress tracking, and AI-driven feedback.</li>
        <li>Solves the problem of low engagement in traditional e-learning by embedding game mechanics and adaptive challenges.</li>
        <li>Acquired 500+ early users within the first week by leveraging social media following and growth expertise; conducted user feedback cycles to refine retention features and improve learning outcomes.</li>
      </ul>
    ),
    images: [
      { src: "/1978.png", alt: "ProdigyQuest Landing Page" },
      { src: "/1979.png", alt: "ProdigyQuest Learning Dashboard" },
      { src: "/1980.png", alt: "ProdigyQuest Learning Hub" },
      { src: "/prodigyquest-4.png", alt: "ProdigyQuest Quiz - Correct Answer" },
      { src: "/prodigyquest-5.png", alt: "ProdigyQuest Quiz - Answer Review" }
    ],
    tech: ["React", "Node.js", "Express", "MongoDB", "AI/ML APIs", "Gamification Engine", "Analytics", "Social Media Integration"],
    tools: ["Figma", "GitHub", "Postman", "Google Analytics", "Firebase", "OpenAI API"],
    metrics: [
      { value: "500+", label: "Week 1 Users" },
      { value: "85%", label: "Retention Rate" },
      { value: "0 to 1", label: "Product Launch" }
    ],
    link: "#"
  },
  {
    id: 3,
    title: "Muvely - 3D Motion Tracking App",
    description: (
      <ul className="enhanced-bullets">
        <li>Led a 3-member team to build and launch a HealthTech, AI-powered 3D motion-tracking fitness app from concept to MVP</li>
        <li>Secured $2,500 seed funding and won 3rd place at CBIC Pitch Night</li>
        <li>Delivered real-time posture correction via computer vision and personalized workout algorithms for 2500+ early users</li>
      </ul>
    ),
    image: "/IMG_6167.png",
    tech: ["Swift", "Core ML", "ARKit", "Computer Vision", "iOS SDK", "Metal Performance Shaders", "AVFoundation"],
    tools: ["Xcode", "TestFlight", "Firebase", "Sketch", "Zeplin", "Git", "Slack"],
    metrics: [
      { value: "3rd Place", label: "CBIC Pitch Night" },
      { value: "$2.5K", label: "Seed Funding" },
      { value: "3-Person", label: "Team Led" }
    ],
    link: "#"
  },
  {
    id: 4,
    title: "Hybrid Electric Vehicle Performance Research",
    description: (
      <ul className="enhanced-bullets">
        <li>Published IEEE research paper analyzing the performance optimization of hybrid electric vehicles through advanced control algorithms.</li>
        <li>Conducted comprehensive analysis of energy management systems and their impact on vehicle efficiency, contributing to sustainable transportation solutions.</li>
      </ul>
    ),
    image: "/HEVExample.png",
    tech: ["MATLAB", "Simulink", "Control Systems", "Signal Processing", "Optimization Algorithms", "Machine Learning"],
    tools: ["MATLAB R2021a", "Simulink", "LaTeX", "IEEE Xplore", "Mendeley", "Excel", "Origin Pro"],
    metrics: [
      { value: "IEEE", label: "Published" },
      { value: "15%", label: "Efficiency Gain" },
      { value: "Research", label: "Innovation" }
    ],
    link: "#"
  }
]

const testimonialsData = [
  {
    id: 1,
    name: "Arun Mathur",
    role: "SaaS Growth Partner - Solution Consulting & Product Strategy, APAC/SEA",
    relationship: "Worked with Devarsh at different companies",
    date: "Jul 2025",
    avatar: "/avatar-arun.png",
    quote: "I've had the privilege of working closely with Devarsh and have always been impressed by his natural leadership, sharp problem-solving skills and creative thinking. He leads with clarity, takes full ownership and brings fresh ideas with a spark of curiosity and humor that makes working with him genuinely fun. His calm presence, strong work ethic and ability to keep the team motivated even during pressure sets him apart. Devarsh doesn't just deliver results, he lifts the vibe of the whole team. I'd strongly recommend him to anyone looking for someone dependable, driven and joy to work with."
  },
  {
    id: 2,
    name: "Kushagra Chaturvedi",
    role: "AVP, Product Management",
    relationship: "Senior to Devarsh, did not manage him directly",
    date: "Jul 2025",
    avatar: "/avatar-kushagra.png",
    quote: "Devarsh is a highly innovative and enthusiastic person, with high ethics value. It's been a great experience to work with him."
  },
  {
    id: 3,
    name: "Rohit Raj Koul",
    role: "Lead Technologist at Amtrak - Ex Software QA Engineer at Jio",
    relationship: "Devarsh's mentor",
    date: "Jan 2026",
    avatar: "/avatar-rohit.png",
    quote: "I've had the pleasure of observing Devarsh work during his last position at Presence, and I can confidently say that he consistently stands out as someone who brings dedication, precision, and a strong work ethic to every project. He is genuinely hardworking and never hesitates to get into the weeds to ensure tasks are completed thoroughly and accurately. One of the qualities I appreciate most is his reliability, he delivers work on time, even when timelines are tight, and always aligns his output precisely with the guidance provided.\n\nBeyond his technical strengths, he brings a mindset of continuous learning and growth. He actively seeks feedback, embraces new challenges, and looks for opportunities to expand his skills. His openness to learning not only elevates his own performance but also contributes positively to the team around him.\n\nOverall, he is someone you can count on for high-quality work, thoughtful execution, and a consistently positive attitude. I highly recommend him for any role or project that values commitment, attention to detail, and a strong desire to grow."
  }
]

const TestimonialShowcase = ({ testimonials }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const autoplayRef = useRef(null)

  const goTo = (index, dir = 1) => {
    setDirection(dir)
    setActiveIndex(index)
  }

  const goNext = () => goTo((activeIndex + 1) % testimonials.length, 1)
  const goPrev = () => goTo((activeIndex - 1 + testimonials.length) % testimonials.length, -1)

  useEffect(() => {
    autoplayRef.current = setTimeout(() => {
      goNext()
    }, 8000)
    return () => clearTimeout(autoplayRef.current)
  }, [activeIndex])

  const active = testimonials[activeIndex]
  const paragraphs = active.quote.split('\n\n')

  return (
    <div className="testimonial-showcase">
      <div className="testimonial-glow"></div>
      <span className="testimonial-quote-mark" aria-hidden="true">&ldquo;</span>

      <div className="testimonial-nav-arrow testimonial-nav-prev" onClick={goPrev} role="button" aria-label="Previous testimonial">
        <span>&#8249;</span>
      </div>
      <div className="testimonial-nav-arrow testimonial-nav-next" onClick={goNext} role="button" aria-label="Next testimonial">
        <span>&#8250;</span>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={active.id}
          className="testimonial-card-premium"
          initial={{ opacity: 0, x: direction * 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -24 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="testimonial-quote-body">
            {paragraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="testimonial-person">
            <div className="testimonial-avatar-ring">
              <img src={active.avatar} alt={active.name} className="testimonial-avatar" loading="lazy" />
            </div>
            <div className="testimonial-person-info">
              <div className="testimonial-name">{active.name}</div>
              <div className="testimonial-role">{active.role}</div>
              <div className="testimonial-meta">{active.relationship} &middot; {active.date}</div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="testimonial-dots">
        {testimonials.map((t, i) => (
          <button
            key={t.id}
            className={`testimonial-dot ${i === activeIndex ? 'active' : ''}`}
            onClick={() => goTo(i, i > activeIndex ? 1 : -1)}
            aria-label={`Show testimonial from ${t.name}`}
          >
            <img src={t.avatar} alt={t.name} loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  )
}

const ExperienceTabs = () => {
  const [activeExperience, setActiveExperience] = useState(0)

  return (
    <div className="projects-tab-container">
      <div className="tab-navigation">
        {experienceData.map((experience, index) => (
          <button
            key={experience.id}
            className={`tab-button ${activeExperience === index ? 'active' : ''}`}
            onClick={() => setActiveExperience(index)}
          >
            {experience.isLogoIcon ? (
              <img src={experience.icon} alt={experience.company} className="tab-logo-icon-large" />
            ) : (
              <>
                <span className="tab-number">{experience.icon}</span>
                <span className="tab-title">{experience.company}</span>
              </>
            )}
          </button>
        ))}
      </div>

      <div className="tab-content-area">
        <AnimatePresence initial={false}>
          <motion.div
            key={activeExperience}
            className="project-tab-content"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            <div className="project-showcase">
              <div className="project-details" style={{ gridColumn: '1 / -1' }}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="timeline-header"
                  style={{ marginBottom: '0.35rem' }}
                >
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '700', background: 'linear-gradient(45deg, #6366f1, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.1rem', lineHeight: '1.2' }}>
                    {experienceData[activeExperience].role} - <a href={experienceData[activeExperience].link} target="_blank" rel="noopener noreferrer" className="company-link" style={{ fontSize: '1.4rem', background: 'linear-gradient(45deg, #6366f1, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textDecoration: 'none' }}>
                      {experienceData[activeExperience].company}
                    </a>
                  </h3>
                  <div className="timeline-meta" style={{ justifyContent: 'flex-start', marginBottom: '0.35rem' }}>
                    <span className="period" style={{ fontSize: '1rem' }}>{experienceData[activeExperience].period}</span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="project-showcase-description"
                  style={{ marginBottom: '0.75rem', fontSize: '0.95rem', lineHeight: '1.5' }}
                >
                  {Array.isArray(experienceData[activeExperience].description) ? (
                    <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                      {experienceData[activeExperience].description.map((point, i) => (
                        <li key={i} style={{ marginBottom: '0.5rem' }}>{point}</li>
                      ))}
                    </ul>
                  ) : (
                    experienceData[activeExperience].description
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="project-showcase-metrics"
                  style={{ marginBottom: '0.75rem' }}
                >
                  {experienceData[activeExperience].metrics.map((metric, index) => (
                    <div key={index} className="metric-showcase">
                      <span className="metric-value">{metric.value}</span>
                      <span className="metric-label">{metric.label}</span>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="timeline-achievements"
                  style={{ marginTop: '0.5rem' }}
                >
                  {experienceData[activeExperience].achievements.map((achievement, index) => (
                    <span key={index} className="achievement-tag">{achievement}</span>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

const EducationTabs = () => {
  const [activeEducation, setActiveEducation] = useState(0)

  return (
    <div className="projects-tab-container">
      <div className="tab-navigation">
        {educationData.map((education, index) => (
          <button
            key={education.id}
            className={`tab-button ${activeEducation === index ? 'active' : ''}`}
            onClick={() => setActiveEducation(index)}
          >
            {education.isLogoIcon ? (
              <img src={education.icon} alt={education.university} className="tab-logo-icon-large" />
            ) : (
              <span className="tab-number">{education.icon}</span>
            )}
            <span className="tab-title">{education.degree}</span>
          </button>
        ))}
      </div>

      <div className="tab-content-area">
        <AnimatePresence initial={false}>
          <motion.div
            key={activeEducation}
            className="project-tab-content"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            <div className="project-showcase">
              <div className="project-details" style={{ gridColumn: '1 / -1' }}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="timeline-header"
                  style={{ marginBottom: '0.35rem' }}
                >
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '700', background: 'linear-gradient(45deg, #6366f1, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '0.1rem', lineHeight: '1.2' }}>
                    {educationData[activeEducation].degree} - <a href={educationData[activeEducation].link} target="_blank" rel="noopener noreferrer" className="company-link" style={{ fontSize: '1.4rem', background: 'linear-gradient(45deg, #6366f1, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textDecoration: 'none' }}>
                      {educationData[activeEducation].university}
                    </a>
                  </h3>
                  <div className="timeline-meta" style={{ justifyContent: 'flex-start', marginBottom: '0.35rem' }}>
                    <span className="period" style={{ fontSize: '1rem' }}>{educationData[activeEducation].period}</span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="project-showcase-description"
                  style={{ marginBottom: '0.75rem', fontSize: '0.95rem', lineHeight: '1.5' }}
                >
                  {educationData[activeEducation].description}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="project-showcase-metrics"
                  style={{ marginBottom: '0.75rem' }}
                >
                  {educationData[activeEducation].metrics.map((metric, index) => (
                    <div key={index} className="metric-showcase">
                      <span className="metric-value">{metric.value}</span>
                      <span className="metric-label">{metric.label}</span>
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="timeline-achievements"
                  style={{ marginTop: '0.5rem' }}
                >
                  {educationData[activeEducation].achievements.map((achievement, index) => (
                    <span key={index} className="achievement-tag">{achievement}</span>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

const ProjectTabs = () => {
  const [activeProject, setActiveProject] = useState(0)

  return (
    <div className="projects-tab-container">
      <div className="tab-navigation">
        {projectsData.map((project, index) => (
          <button
            key={project.id}
            className={`tab-button ${activeProject === index ? 'active' : ''}`}
            onClick={() => setActiveProject(index)}
          >
            <span className="tab-number">{index + 1}</span>
            <span className="tab-title">{project.title}</span>
            {project.isLive && <span className="tab-live-dot" title="Live product" />}
          </button>
        ))}
      </div>

      <div className="tab-content-area">
        <AnimatePresence initial={false}>
          <motion.div
            key={activeProject}
            className="project-tab-content"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            <div className="project-showcase">
              <div className="project-visual">
                <div className="project-image-container">
                  {projectsData[activeProject].demo ? (
                    <TutorWeightsDemo />
                  ) : projectsData[activeProject].images ? (
                    <ImageSlideshow
                      images={projectsData[activeProject].images}
                      interval={3000}
                      className="project-slideshow"
                    />
                  ) : (
                    <img
                      src={projectsData[activeProject].image}
                      alt={projectsData[activeProject].title}
                      className="project-showcase-image"
                      onError={(e) => {
                        e.target.src = '/112233.png'; // fallback image
                      }}
                    />
                  )}
                </div>

                {/* Tech Stack Section - Below Image */}
                <div className="project-tech-section">
                  <div className="tech-section-container">
                    <div className="tech-section">
                      <div className="tech-section-header">Tech Stack</div>
                      <div className="tech-badges-grid">
                        {projectsData[activeProject].tech.map((tech, index) => (
                          <motion.div
                            key={tech}
                            className="tech-badge tech-stack"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            {tech}
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {projectsData[activeProject].tools && (
                      <div className="tech-section">
                        <div className="tech-section-header">Tools</div>
                        <div className="tech-badges-grid">
                          {projectsData[activeProject].tools.map((tool, index) => (
                            <motion.div
                              key={tool}
                              className="tech-badge tech-tools"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3 + (index * 0.1) }}
                            >
                              {tool}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="project-details">
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="project-showcase-title"
                >
                  {projectsData[activeProject].title}
                  {projectsData[activeProject].isLive && (
                    <a
                      href={projectsData[activeProject].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="live-badge"
                      title="Open the live product"
                    >
                      <span className="live-dot" />
                      LIVE
                    </a>
                  )}
                </motion.h3>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="project-showcase-description"
                >
                  {typeof projectsData[activeProject].description === 'string'
                    ? projectsData[activeProject].description
                    : projectsData[activeProject].description}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="project-showcase-metrics"
                >
                  {projectsData[activeProject].metrics.map((metric, index) => (
                    <div key={index} className="metric-showcase">
                      <span className="metric-value">{metric.value}</span>
                      <span className="metric-label">{metric.label}</span>
                    </div>
                  ))}
                </motion.div>

                {projectsData[activeProject].link !== "#" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="project-showcase-link"
                  >
                    <a
                      href={projectsData[activeProject].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link-primary"
                    >
                      {projectsData[activeProject].isLive
                        ? '🚀 Try AppliFirst Live →'
                        : 'View Project →'}
                    </a>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

const ErasingText = ({ texts, typingSpeed = 100, erasingSpeed = 50, delayBetween = 2000 }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0)
  const [currentText, setCurrentText] = useState("")
  const [isErasing, setIsErasing] = useState(false)
  const [charIndex, setCharIndex] = useState(0)

  useEffect(() => {
    const handleTyping = () => {
      const fullText = texts[currentTextIndex]

      if (!isErasing && charIndex < fullText.length) {
        setCurrentText(fullText.substring(0, charIndex + 1))
        setCharIndex(prev => prev + 1)
      } else if (isErasing && charIndex > 0) {
        setCurrentText(fullText.substring(0, charIndex - 1))
        setCharIndex(prev => prev - 1)
      } else if (!isErasing && charIndex === fullText.length) {
        setTimeout(() => setIsErasing(true), delayBetween)
      } else if (isErasing && charIndex === 0) {
        setIsErasing(false)
        setCurrentTextIndex(prev => (prev + 1) % texts.length)
      }
    }

    const speed = isErasing ? erasingSpeed : typingSpeed
    const timer = setTimeout(handleTyping, speed)
    return () => clearTimeout(timer)
  }, [charIndex, isErasing, currentTextIndex, texts, typingSpeed, erasingSpeed, delayBetween])

  return (
    <span className="erasing-text">
      {currentText}
      <span className="cursor-blink">|</span>
    </span>
  )
}

const ParticleBackground = () => {
  return (
    <div className="particle-background">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="particle"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
            animationDuration: `${15 + Math.random() * 10}s`
          }}
        />
      ))}
    </div>
  )
}

const stkStyle = (i) => ({ '--i': i })

const StackedExperience = () => (
  <StackList>
    {experienceData.map((exp, i) => (
      <article className="stk-card" key={exp.id} style={stkStyle(i)}>
        <div className="stk-meta">
          <span className="stk-index">{String(i + 1).padStart(2, '0')}</span>
          <span className="stk-logo-plate">
            {exp.isLogoIcon
              ? <img src={exp.icon} alt={exp.company} className="stk-logo" loading="lazy" decoding="async" />
              : <span className="stk-logo-text">{exp.company}</span>}
          </span>
          <span className="stk-period">{exp.period}</span>
        </div>
        <div className="stk-body">
          <h3 className="stk-title">
            {exp.role} <span className="stk-sep">at</span>{' '}
            <a href={exp.link} target="_blank" rel="noopener noreferrer" className="stk-company" title={`Visit ${exp.company}`}>{exp.company}<span className="stk-company-arrow" aria-hidden="true">↗</span></a>
          </h3>
          <div className="stk-desc">
            {Array.isArray(exp.description) ? (
              <ul>{exp.description.map((p, k) => <li key={k}>{p}</li>)}</ul>
            ) : exp.description}
          </div>
          <div className="stk-tags">
            {exp.achievements.map((a, k) => <span key={k} className="stk-tag">{a}</span>)}
          </div>
        </div>
        <div className="stk-metrics">
          {exp.metrics.map((m, k) => (
            <div key={k} className={`stk-metric ${k === 0 ? 'big' : ''}`}>
              <span className="stk-metric-value">{m.value}</span>
              <span className="stk-metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      </article>
    ))}
  </StackList>
)

// Visual + a context-specific "click me" cue. The cue differs by project:
// an interactive demo gets a "flip the tab" prompt and a glowing frame that
// calms down after the first touch; a live product gets a pinging link chip;
// anything else with a link gets a quiet "see it in the wild" chip.
const ProjectVisual = ({ p }) => {
  const [touched, setTouched] = useState(false)
  const hot = (p.demo && !touched) || p.isLive
  return (
    <div className={`stk-visual-wrap ${hot ? 'is-hot' : ''}`} onPointerDown={() => setTouched(true)}>
      {p.demo && (
        <div className={`stk-nudge demo ${touched ? 'done' : ''}`}>
          <span className="nudge-arrow" aria-hidden="true">↓</span>
          {touched
            ? <>Nice. Now try <b>Guarded prompt</b> if you haven't</>
            : <>Interactive. Flip to <b>Guarded prompt</b> and watch 47 become 95</>}
        </div>
      )}
      {p.isLive && (
        <a href={p.link} target="_blank" rel="noopener noreferrer" className="stk-nudge live">
          <span className="nudge-ping" aria-hidden="true" />
          Just screenshots here. The real thing is live <span className="nudge-out" aria-hidden="true">↗</span>
        </a>
      )}
      {!p.demo && !p.isLive && p.link !== '#' && (
        <a href={p.link} target="_blank" rel="noopener noreferrer" className="stk-nudge link">
          See it in the wild <span className="nudge-out" aria-hidden="true">↗</span>
        </a>
      )}
      <div className={`stk-visual ${p.demo ? 'is-demo' : ''}`}>
        {p.demo ? (
          <TutorWeightsDemo />
        ) : p.images ? (
          <ImageSlideshow images={p.images} interval={3000} className="project-slideshow" />
        ) : (
          <img
            src={p.image}
            alt={p.title}
            className="stk-img"
            onError={(e) => { e.target.src = '/112233.png' }}
          />
        )}
      </div>
    </div>
  )
}

const StackedEducation = () => (
  <StackList>
    {educationData.map((edu, i) => (
      <article className="stk-card" key={edu.id} style={stkStyle(i)}>
        <div className="stk-meta">
          <span className="stk-index">{String(i + 1).padStart(2, '0')}</span>
          <span className="stk-logo-plate">
            {edu.isLogoIcon
              ? <img src={edu.icon} alt={edu.university} className="stk-logo" loading="lazy" decoding="async" />
              : <span className="stk-logo-text">{edu.university}</span>}
          </span>
          <span className="stk-period">{edu.period}</span>
        </div>
        <div className="stk-body">
          <h3 className="stk-title">
            {edu.degree} <span className="stk-sep">at</span>{' '}
            <a href={edu.link} target="_blank" rel="noopener noreferrer" className="stk-company" title={`Visit ${edu.university}`}>{edu.university}<span className="stk-company-arrow" aria-hidden="true">↗</span></a>
          </h3>
          <div className="stk-desc"><p>{edu.description}</p></div>
          <div className="stk-tags">
            {edu.achievements.map((a, k) => <span key={k} className="stk-tag">{a}</span>)}
          </div>
        </div>
        <div className="stk-metrics">
          {edu.metrics.map((m, k) => (
            <div key={k} className={`stk-metric ${k === 0 ? 'big' : ''}`}>
              <span className="stk-metric-value">{m.value}</span>
              <span className="stk-metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      </article>
    ))}
  </StackList>
)

const StackedProjects = () => (
  <StackList>
    {projectsData.map((p, i) => (
      <article className="stk-card stk-project" key={p.id} style={stkStyle(i)}>
        <ProjectVisual p={p} />
        <div className="stk-body">
          <div className="stk-meta">
            <span className="stk-index">{String(i + 1).padStart(2, '0')}</span>
            {p.isLive && (
              <a href={p.link} target="_blank" rel="noopener noreferrer" className="live-badge" title="Open the live product">
                <span className="live-dot" />LIVE
              </a>
            )}
          </div>
          <h3 className="stk-title">{p.title}</h3>
          <div className="stk-desc">{p.description}</div>
          <div className="stk-metrics row">
            {p.metrics.map((m, k) => (
              <div key={k} className="stk-metric">
                <span className="stk-metric-value">{m.value}</span>
                <span className="stk-metric-label">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="stk-tags">
            {p.tech.map((t) => <span key={t} className="stk-tag">{t}</span>)}
            {p.tools && p.tools.map((t) => <span key={t} className="stk-tag alt">{t}</span>)}
          </div>
          {p.link !== '#' && (
            <a href={p.link} target="_blank" rel="noopener noreferrer" className="stk-cta">
              {p.isLive ? 'Try AppliFirst Live' : 'View Project'} <span className="stk-cta-arrow" aria-hidden="true">→</span>
            </a>
          )}
        </div>
      </article>
    ))}
  </StackList>
)

const awardsData = [
  {
    key: 'umbc',
    badge: '🏆', year: 'President',
    title: 'Outstanding Graduate Student Organization Award Winner (May 2024)',
    org: 'President, Product Management Club (UMBC)',
    text: "Outstanding Graduate Student Organization Award Winner - Recognized for building UMBC's largest student community from ground up. Organized the Product Hackathon with 60+ teams participation, driving innovation across the Baltimore startup ecosystem. Grew student member participation from 10 to 500+ creating the largest PM community at UMBC. Organized monthly PM workshops with consistent 30+ student attendance, bridging academic learning with industry practice.",
    images: [
      { src: "./umbc-pm-club-award.jpg", alt: "UMBC Product Management Club Award" },
      { src: "./pm-club-1.jpeg", alt: "PM Club Event - Team Building" },
      { src: "./pm-club-2.jpeg", alt: "PM Club Event - Workshop" }
    ],
    cls: 'pm-club-slideshow',
    stats: [["60+", "Hackathon Teams"], ["500+", "Community Members"], ["30+", "Monthly Attendees"], ["12", "Industry Workshops"]]
  },
  {
    key: 'speaker',
    badge: '🎤', year: 'Speaker',
    title: 'Guest Speaker (Apr 2023)',
    org: 'Fashion & Lifestyle Retail Convention',
    text: "Invited as a keynote speaker to share insights on digital transformation in retail and fintech innovation. Presented alongside industry leaders from BharatX and other innovative startups to an audience of retail executives and entrepreneurs.",
    images: [
      { src: "./speaker-1.jpg", alt: "Guest Speaker at Fashion & Lifestyle Retail Convention" },
      { src: "./speaker-2.jpg", alt: "Panel Discussion at Fashion & Lifestyle Retail Convention" }
    ],
    cls: 'speaker-slideshow',
    stats: [["80+", "Startups Participated"], ["20+", "Sponsors"], ["200+", "Total Participation"], ["5+", "Fortune 500 Companies"]]
  }
]

const StackedAwards = () => (
  <StackList>
    {awardsData.map((a, i) => (
      <article className="stk-card stk-project" key={a.key} style={stkStyle(i)}>
        <div className="stk-visual">
          <ImageSlideshow images={a.images} interval={3000} className={a.cls} />
        </div>
        <div className="stk-body">
          <div className="stk-meta">
            <span className="stk-index">{String(i + 1).padStart(2, '0')}</span>
            <span className="stk-tag">{a.badge} {a.year}</span>
          </div>
          <h3 className="stk-title">{a.title}</h3>
          <div className="stk-org">{a.org}</div>
          <div className="stk-desc"><p>{a.text}</p></div>
          <div className="stk-metrics row">
            {a.stats.map(([v, l]) => (
              <div key={l} className="stk-metric">
                <span className="stk-metric-value">{v}</span>
                <span className="stk-metric-label">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </article>
    ))}
  </StackList>
)

const FloatingCard = ({ children, className = "", delay = 0 }) => {
  return (
    <div className={`floating-card ${className}`} style={{ animationDelay: `${delay}s` }}>
      {children}
    </div>
  )
}

const ImageSlideshow = ({ images, interval = 3000, className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const timerRef = useRef(null)
  const imagesLengthRef = useRef(images.length)

  useEffect(() => {
    imagesLengthRef.current = images.length
  }, [images.length])

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imagesLengthRef.current)
    }, interval)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [interval])

  const handleDotClick = (index) => {
    setCurrentIndex(index)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imagesLengthRef.current)
    }, interval)
  }

  // Read every image's natural shape up front so the frame can resize to the
  // current slide: no letterboxing, no grey bars, and nothing gets cropped.
  const [ratios, setRatios] = useState({})
  useEffect(() => {
    let alive = true
    images.forEach((image, i) => {
      const probe = new Image()
      probe.onload = () => {
        if (alive && probe.naturalWidth && probe.naturalHeight) {
          setRatios((r) => ({ ...r, [i]: probe.naturalWidth / probe.naturalHeight }))
        }
      }
      probe.src = image.src
    })
    return () => { alive = false }
  }, [images])
  const ratio = ratios[currentIndex] || ratios[0] || 16 / 10

  return (
    <div
      className={`slideshow-container fit-slides ${className}`}
      style={{ aspectRatio: ratio, width: `min(100%, calc(56vh * ${ratio}))` }}
    >
      {images.map((image, index) => (
        <img
          key={index}
          src={image.src}
          alt={image.alt}
          className={`slideshow-image ${index === currentIndex ? 'active' : ''}`}
          loading="lazy"
          onError={(e) => {
            console.error('Image failed to load:', image.src);
            e.target.src = '/112233.png';
          }}
        />
      ))}
      <div className="slideshow-indicators">
        {images.map((_, index) => (
          <div
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </div>
  )
}

export default function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [isVisible, setIsVisible] = useState({})
  const [scrollY, setScrollY] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [showBottomArrow, setShowBottomArrow] = useState(false)

  useEffect(() => {
    // Scroll to top on page load/refresh - enhanced version
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0

    // Additional scroll to top after a short delay to ensure DOM is ready
    const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 100)


    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.id) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: entry.isIntersecting
            }))
          }
        })
      },
      { threshold: 0.1 }
    )

    const sections = document.querySelectorAll('section[id]')
    sections.forEach((section) => {
      observer.observe(section)
    })

    // Scroll handler
    const handleScroll = () => {
      setScrollY(window.scrollY)

      // Show scroll-to-top button when user scrolls down past 300px
      const shouldShow = window.scrollY > 300
      setShowScrollTop(shouldShow)

      // Show bottom arrow when user is at the very top, hide when scrolled down significantly
      const documentHeight = document.documentElement.scrollHeight
      const windowHeight = window.innerHeight
      const scrollPosition = window.scrollY
      const isAtTop = scrollPosition < 100 // Show when user is within 100px from top
      const hasScrollableContent = documentHeight > windowHeight * 1.5 // Only show if page is long enough

      setShowBottomArrow(isAtTop && hasScrollableContent)
    }

    window.addEventListener('scroll', handleScroll)

    // Cleanup function
    return () => {
      clearTimeout(scrollTimeout)
      if (observer) {
        observer.disconnect()
      }
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const roles = [
    "Product Manager",
    "Project Manager",
    "Program Manager",
    "Creative PM",
    "Product Marketing",
    "Business Leader",
    "Agile PM"
  ]

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    })
  }

  if (isLoading) {
    return <LoadingScreen onComplete={() => setIsLoading(false)} />
  }

  return (
    <div className="portfolio-dark">
      <ParticleBackground />
      <ScrollFX />

      {/* Navigation */}
      <nav className="nav-glass">
        <div className="nav-container">
          <MagicalLogo>
            <div className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>
              <span className="logo-text">Devarsh</span>
              <span className="logo-accent">Wali</span>
            </div>
          </MagicalLogo>
          <div className="nav-menu">
            <a href="#hero" className="nav-link" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Home</a>
            <a href="#experience" className="nav-link">Experience</a>
            <a href="#projects" className="nav-link">Projects</a>
            <a href="#achievement" className="nav-link">Recognition</a>
            <a href="#blogs" className="nav-link">Blogs</a>
            <a href="#education" className="nav-link">Education</a>
            <a href="#skills" className="nav-link">Skills</a>
            <a href="#recommendations" className="nav-link">Recommendations</a>
            <a href="#contact" className="nav-link-cta">Hire Me!</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-dark" id="hero">
        <div className="hero-mesh" aria-hidden="true"><i></i><i></i><i></i></div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-status"><span className="pulse-dot"></span>OPEN TO PM ROLES</div>
            <div className="hero-greeting">
              <ErasingText
                texts={[
                  "Namaste, I'm",
                  "Nǐ hǎo, I'm",
                  "Hallo, I'm",
                  "Bonjour, I'm",
                  "Привет, I'm",
                  "Hello, I'm"
                ]}
                className="greeting-text"
                typingSpeed={120}
                erasingSpeed={60}
                delayBetween={1500}
              />
            </div>
            <h1 className="hero-name">
              <span className="name-first">Devarsh</span>
              <span className="name-last">Wali</span>
            </h1>
            <div className="hero-role">
              <span className="role-prefix">Product Manager who</span>
              <RotatingVerb />
            </div>
            <p className="hero-description">
              I build AI products people actually use, taking ideas from zero to launch across EdTech, FinTech and HealthTech. Engineering-management background, so I'm happiest where the problem is messy and the ownership is high.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">$1.2M+</span>
                <span className="stat-label">MRR Launched (0→1)</span>
                <span className="stat-company">BYJU'S</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100M+</span>
                <span className="stat-label">Registered Learners</span>
                <span className="stat-company">BYJU'S</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">+7.6</span>
                <span className="stat-label">Net Promoter Score Lift</span>
                <span className="stat-company">Barclays</span>
              </div>
            </div>
            <div className="hero-buttons">
              <a href="#experience" className="btn-primary-glow">
                <span>View Experience</span>
                <div className="btn-glow"></div>
              </a>
              <a href="#contact" className="btn-primary-glow">
                <span>Book a 1:1</span>
                <div className="btn-glow"></div>
              </a>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=devarshwali@gmail.com&su=Hello%20Devarsh&body=Hi%20Devarsh%2C%0A%0AI%20came%20across%20your%20portfolio%20and%20would%20like%20to%20connect.%0A%0A"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-glow"
                title="Opens Gmail with devarshwali@gmail.com filled in"
              >
                <span>Email Me</span>
                <div className="btn-glow"></div>
              </a>
            </div>
          </div>

          <div className="hero-visual">
            <div className="profile-container">
              <img
                src={HERO_IMG}
                alt="Devarsh Wali"
                className="profile-image"
                width="802"
                height="1003"
                fetchpriority="high"
                decoding="async"
                onError={(e) => {
                  if (!e.target.dataset.fallback) {
                    e.target.dataset.fallback = '1';
                    e.target.src = `/devarsh-cutout.png?v=${__BUILD_ID__}`;
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <ProofFeed />

      <Manifesto />

      {/* Experience Section */}
      <section id="experience" className={`section-dark ${isVisible.experience ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">My Professional Journey</h2>
            <p className="section-subtitle">Six roles, four industries, one habit: talk to users, ship, measure.</p>
          </div>

          <StackedExperience />
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className={`section-dark ${isVisible.projects ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">Projects</h2>
            <p className="section-subtitle">Things I built because I couldn't stop thinking about them.</p>
          </div>

          <StackedProjects />
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section id="achievement" className={`section-dark ${isVisible.achievement ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">Awards & Recognition</h2>
            <p className="section-subtitle">The non-PM wins: a campus community and a few microphones.</p>
          </div>

          <StackedAwards />
        </div>
      </section>

      {/* Blog Section */}
      <section id="blogs" className={`section-dark ${isVisible.blogs ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">Blogs</h2>
            <p className="section-subtitle">Questions I got curious about, answered with data.</p>
          </div>

          <div className="blogs-grid">
            <FloatingCard className="blog-card" delay={0.2}>
              <div className="blog-image">
                <img src="/123.png" alt="EdTech Innovation Post" loading="lazy" />
              </div>
              <div className="blog-content">
                <div className="blog-header">
                  <span className="blog-category">EdTech</span>
                  <span className="blog-date">Recent</span>
                </div>
                <h3>Which Industry VCs Are Betting On: The Data-Driven Truth Behind Startup Success</h3>
                <p>
                  Decoded Y Combinator's startup selection patterns using Python and real market data to predict the next big winners.
                </p>
                <div className="blog-meta">
                  <a href="https://www.linkedin.com/posts/devarshwali_edtech-innovation-technology-activity-7348535421309505536-xPQz?utm_source=share&utm_medium=member_desktop&rcm=ACoAABkpSE0Bk2t-E9LDz1_PATTShps3apN6R8o" target="_blank" rel="noopener noreferrer" className="blog-link">Read on LinkedIn →</a>
                </div>
              </div>
            </FloatingCard>

            <FloatingCard className="blog-card" delay={0.4}>
              <div className="blog-image">
                <img src="/54321.png" alt="Data Skills Python Post" loading="lazy" />
              </div>
              <div className="blog-content">
                <div className="blog-header">
                  <span className="blog-category">Data Skills</span>
                  <span className="blog-date">Recent</span>
                </div>
                <h3>I Analyzed 200+ Countries' Job Market Data - Here's What LinkedIn Doesn't Want You to Know</h3>
                <p>
                  Scraped job data from multiple platforms to reveal hidden patterns that could change your job search strategy forever.
                </p>
                <div className="blog-meta">
                  <a href="https://www.linkedin.com/posts/devarshwali_dataskills-python-learninginpublic-activity-7346383852078059521-2Xs0?utm_source=share&utm_medium=member_desktop&rcm=ACoAABkpSE0Bk2t-E9LDz1_PATTShps3apN6R8o" target="_blank" rel="noopener noreferrer" className="blog-link">Read on LinkedIn →</a>
                </div>
              </div>
            </FloatingCard>

            <FloatingCard className="blog-card" delay={0.6}>
              <div className="blog-image">
                <img src="/12345.png" alt="Product Management AI Post" loading="lazy" />
              </div>
              <div className="blog-content">
                <div className="blog-header">
                  <span className="blog-category">Product Management</span>
                  <span className="blog-date">Recent</span>
                </div>
                <h3>Why AI Will Never Replace Product Managers (But Will Make Them Unstoppable)</h3>
                <p>
                  The truth about AI's role in product management - it's not about replacement, it's about amplification of human intuition.
                </p>
                <div className="blog-meta">
                  <a href="https://www.linkedin.com/posts/devarshwali_productmanagement-ai-innovation-activity-7306783467512418304-zTov?utm_source=share&utm_medium=member_desktop&rcm=ACoAABkpSE0Bk2t-E9LDz1_PATTShps3apN6R8o" target="_blank" rel="noopener noreferrer" className="blog-link">Read on LinkedIn →</a>
                </div>
              </div>
            </FloatingCard>
          </div>

          <div className="more-reads-section">
            <div className="more-reads-compact">
              <span className="more-reads-text">More insights</span>
              <a href="https://www.linkedin.com/in/devarshwali/recent-activity/all/" target="_blank" rel="noopener noreferrer" className="view-all-link">
                View All Posts →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className={`section-dark ${isVisible.education ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">Education</h2>
            <p className="section-subtitle">Where the engineering brain and the PM brain got trained.</p>
          </div>

          <StackedEducation />
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className={`section-dark ${isVisible.skills ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">Skills & Certifications</h2>
            <p className="section-subtitle">The tools I use daily, plus the credentials that back them up.</p>
          </div>

          {/* Certifications Banner */}
          <div className="certifications-banner" style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <div className="certifications-row" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
              <span className="cert-badge pmp" style={{ padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: '600', color: '#fff', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)' }}>
                PMP Certified
              </span>
              <span className="cert-badge ibm" style={{ padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: '600', color: '#fff', background: 'linear-gradient(135deg, #0f62fe 0%, #8a3ffc 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 15px rgba(15, 98, 254, 0.3)' }}>
                IBM AI Product Manager
              </span>
              <span className="cert-badge google" style={{ padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: '600', color: '#fff', background: 'linear-gradient(135deg, #34a853 0%, #4285f4 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 15px rgba(52, 168, 83, 0.3)' }}>
                Google AI
              </span>
              <span className="cert-badge google" style={{ padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: '600', color: '#fff', background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 15px rgba(66, 133, 244, 0.3)' }}>
                Google IT Automation with Python
              </span>
            </div>
          </div>

          <div className="skills-visual-grid">
            {/* All Tools in Enhanced Grid */}
            <div className="tools-row">
              <FloatingCard className="tool-card" delay={0.1}>
                <img src="/logos/jira-final-new-logo.png" alt="Jira" className="tool-logo" loading="lazy" />
                <span className="tool-name">Jira</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.15}>
                <img src="/logos/tableau-new-logo.png" alt="Tableau" className="tool-logo" loading="lazy" />
                <span className="tool-name">Tableau</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.2}>
                <img src="/logos/python-new-logo.png" alt="Python" className="tool-logo" loading="lazy" />
                <span className="tool-name">Python</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.25}>
                <img src="/logos/salesforce-new-logo.png" alt="Salesforce" className="tool-logo" loading="lazy" />
                <span className="tool-name">Salesforce</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.3}>
                <img src="/logos/hubspot-new-logo.png" alt="HubSpot" className="tool-logo" loading="lazy" />
                <span className="tool-name">HubSpot</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.35}>
                <img src="/logos/google-analytics-new-logo.png" alt="Google Analytics" className="tool-logo" loading="lazy" />
                <span className="tool-name">Google Analytics</span>
              </FloatingCard>
            </div>

            <div className="tools-row">
              <FloatingCard className="tool-card" delay={0.4}>
                <img src="/logos/mysql-new-logo.png" alt="MySQL" className="tool-logo" loading="lazy" />
                <span className="tool-name">MySQL</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.45}>
                <img src="/logos/power-bi-new-logo.png" alt="Power BI" className="tool-logo" loading="lazy" />
                <span className="tool-name">Power BI</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.5}>
                <img src="/logos/figma-new-logo.png" alt="Figma" className="tool-logo" loading="lazy" />
                <span className="tool-name">Figma</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.55}>
                <img src="/logos/notion123.png" alt="Notion" className="tool-logo" loading="lazy" />
                <span className="tool-name">Notion</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.6}>
                <img src="/logos/replit-new-logo.png" alt="Replit" className="tool-logo" loading="lazy" />
                <span className="tool-name">Replit</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.65}>
                <img loading="lazy" decoding="async" width="24" height="24" src="/logos/github123.png" alt="GitHub" className="tool-logo" loading="lazy" />
                <span className="tool-name">GitHub</span>
              </FloatingCard>
            </div>

            <div className="tools-row">
              <FloatingCard className="tool-card" delay={0.9}>
                <img src="/logos/chatgpt123.png" alt="ChatGPT" className="tool-logo" loading="lazy" />
                <span className="tool-name">ChatGPT</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={0.95}>
                <img src="/logos/claude-new-logo.png" alt="Claude" className="tool-logo" loading="lazy" />
                <span className="tool-name">Claude</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={1.0}>
                <img src="/logos/gemini-new-logo.png" alt="Gemini" className="tool-logo" loading="lazy" />
                <span className="tool-name">Gemini</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={1.05}>
                <img src="/perplexity123.png" alt="Perplexity" className="tool-logo" loading="lazy" />
                <span className="tool-name">Perplexity</span>
              </FloatingCard>

              <FloatingCard className="tool-card" delay={1.1}>
                <img src="/logos/llama-new-logo.png" alt="Llama" className="tool-logo" loading="lazy" />
                <span className="tool-name">Llama</span>
              </FloatingCard>


            </div>

            {/* Management Frameworks */}
            <div className="frameworks-standalone-container">
              <h3 className="frameworks-title">Management Frameworks</h3>
              <FloatingCard className="frameworks-container" delay={0.6}>
                <div className="frameworks-grid">
                  <div className="framework-item agile">
                    <div className="framework-icon">
                      <span>⚡</span>
                    </div>
                    <div className="framework-content">
                      <h4>Agile & Scrum</h4>
                      <p>Sprint Planning • Daily Standups • Retrospectives</p>
                    </div>
                  </div>

                  <div className="framework-item roadmap">
                    <div className="framework-icon">
                      <span>🗺️</span>
                    </div>
                    <div className="framework-content">
                      <h4>Product Roadmapping</h4>
                      <p>Strategic Planning • Feature Prioritization • Vision Alignment</p>
                    </div>
                  </div>

                  <div className="framework-item gtm">
                    <div className="framework-icon">
                      <span>🚀</span>
                    </div>
                    <div className="framework-content">
                      <h4>Go-to-Market Strategy</h4>
                      <p>Market Analysis • Launch Planning • Growth Tactics</p>
                    </div>
                  </div>

                  <div className="framework-item design">
                    <div className="framework-icon">
                      <span>💡</span>
                    </div>
                    <div className="framework-content">
                      <h4>Design Thinking</h4>
                      <p>User Research • Ideation • Prototyping • Testing</p>
                    </div>
                  </div>
                </div>
              </FloatingCard>
            </div>
          </div>
        </div>
      </section>

      {/* Recommendations Section */}
      <section id="recommendations" className={`section-dark testimonials-section ${isVisible.recommendations ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-gradient">What People Say</h2>
            <p className="section-subtitle">Real LinkedIn recommendations from people who've worked with Devarsh</p>
          </div>

          <TestimonialShowcase testimonials={testimonialsData} />
        </div>
      </section>

      {/* Hire Me Section */}
      <section id="contact" className={`section-dark hire-me-section ${isVisible.contact ? 'animate-in' : ''}`}>
        <div className="container">
          <div className="hire-me-hero">
            <div className="hire-me-badge">
              <span className="availability-pulse"></span>
              <span className="availability-text">Available for Hire</span>
            </div>
            <h2 className="hire-me-title">
              Got a Messy Problem and a Deadline?
            </h2>
            <p className="hire-me-subtitle">
              Teams usually call me when the users are real, the problem is fuzzy and the clock is loud. I bring
              the customer calls, a working prototype and the numbers to back the decision. If that sounds like your week,
              let's talk.
            </p>
          </div>

          <div className="cta-single-card">
            <div className="cta-call-card">
              <div className="cta-call-icon-wrap">
                <span className="cta-call-icon">📞</span>
              </div>
              <h3 className="cta-call-heading">Let's Talk Product</h3>
              <p className="cta-call-desc">15 min is all it takes. Let's see if there's a fit.</p>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=devarshwali@gmail.com&su=Interview%20Request%20%E2%80%93%20Let%E2%80%99s%20Schedule%20a%20Call&body=Hi%20Devarsh%2C%0A%0AI%20came%20across%20your%20portfolio%20and%20would%20love%20to%20schedule%20a%20quick%20call.%0A%0AHere%20are%20a%20few%20times%20that%20work%20for%20me%3A%0A%0ALooking%20forward%20to%20connecting!%0A%0ABest%2C"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-call-button"
              >
                <span>Book a 1:1 Call</span>
                <span className="cta-call-arrow">→</span>
              </a>
              <div className="cta-social-row">
                <a href="https://linkedin.com/in/devarshwali" target="_blank" rel="noopener noreferrer" className="cta-social-link">
                  <img loading="lazy" decoding="async" width="24" height="24" src="/logos/linkedin-official-new.svg" alt="LinkedIn" />
                </a>
                <a href="https://instagram.com/devarshwalimagic" target="_blank" rel="noopener noreferrer" className="cta-social-link">
                  <img loading="lazy" decoding="async" width="24" height="24" src="/logos/instagram-official-new.svg" alt="Instagram" />
                </a>
                <a href="https://github.com/devarshwali" target="_blank" rel="noopener noreferrer" className="cta-social-link">
                  <img loading="lazy" decoding="async" width="24" height="24" src="/logos/github123.png" alt="GitHub" />
                </a>
              </div>
            </div>
          </div>

          </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          className="scroll-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <span className="scroll-arrow">↑</span>
        </button>
      )}

      {/* Footer */}
      <footer className="footer-dark">
        <div className="container">
          <div className="footer-content">
            <p className="footer-copyright">&copy; 2026 Devarsh Wali. Built with passion and creativity.</p>
          </div>
          <div className="ghost-wordmark" aria-hidden="true">DEVARSH WALI</div>
        </div>
      </footer>

      {/* AI Agent */}
      <ErrorBoundary>
        <DevarshAgent />
      </ErrorBoundary>
    </div>
  )
}
